import type { OrderFiltersOptionsResponse } from '@contracts/order/OrderFiltersOptionsResponse';
import type { Filter } from '@src/common/components/Filters';
import type { OrderTab } from '@src/orders/domain/OrderTab';
import { OrdersClient } from '@src/orders/services/OrdersClient';
import { OrderClientFilter } from '@src/permits/permitSearch/filters/OrderClientFilter';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface OrderFilters {
  status?: string;
  client?: string;
  assignee?: string;
}

export const useOrderFiltersOptions = (tab: OrderTab) => {
  const { t } = useTranslation();
  const [selectedFilters, setSelectedFilters] = useState<OrderFilters>({});

  const { data } = useQuery<OrderFiltersOptionsResponse>({
    queryKey: ['orders.filters.options.get'],
    queryFn: () => OrdersClient.getFiltersOptions(tab),
  });

  const filters: Filter<OrderFilters>[] = data
    ? [
        {
          key: 'status',
          options: data.status?.map((status) => ({
            label: t(status.toLowerCase()),
            value: status,
          })),
        },
        {
          key: 'client',
          options: data.clients?.map((client) => ({
            label: client.name,
            value: client.id,
          })),
          renderFilter: (field) => {
            const clientId =
              typeof field.value === 'string' ? field.value : null;
            const selectedClient = data.clients?.find((c) => c.id === clientId);
            const selectedOptions = selectedClient
              ? [{ name: selectedClient.name, value: selectedClient.id }]
              : [];

            return (
              <OrderClientFilter
                onFilterChange={(filters) =>
                  field.onChange(filters.client?.[0] ?? null)
                }
                value={selectedOptions}
                options={data.clients}
              />
            );
          },
        },
        {
          key: 'assignee',
          options: data.assignees?.map((assignee) => ({
            label: assignee.name,
            value: assignee.id,
          })),
        },
      ]
    : [];

  return {
    filters,
    selectedFilters,
    selectFilters: setSelectedFilters,
  };
};
